/**
 * Configuration des modulateurs d'éligibilité BATICO (Super Admin).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Save, RotateCcw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeader } from '../services/tokenService';
import BaticoConfigTabs from './BaticoConfigTabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BaticoEligibilityConfigPanel({ variant = 'annuel' }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/batico/eligibility/config`, { headers: getAuthHeader() });
      setConfig(res.data);
    } catch (e) {
      toast.error('Erreur de chargement : ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/batico/eligibility/config`, config, { headers: getAuthHeader() });
      toast.success('Configuration enregistrée');
    } catch (e) {
      toast.error('Erreur : ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Réinitialiser TOUTE la configuration aux valeurs par défaut ?')) return;
    setSaving(true);
    try {
      await axios.post(`${API}/batico/eligibility/config/reset`, {}, { headers: getAuthHeader() });
      await load();
      toast.success('Configuration réinitialisée');
    } catch (e) {
      toast.error('Erreur : ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const updateList = (key, idx, newItem) => {
    setConfig((c) => {
      const arr = [...(c[key] || [])];
      arr[idx] = newItem;
      return { ...c, [key]: arr };
    });
  };

  // Ajout / suppression d'éléments dans une liste paramétrable (ex: territoires)
  const addItem = (key, defaults = {}) => {
    setConfig((c) => ({ ...c, [key]: [...(c[key] || []), defaults] }));
  };

  const removeItem = (key, idx) => {
    setConfig((c) => {
      const arr = [...(c[key] || [])];
      arr.splice(idx, 1);
      return { ...c, [key]: arr };
    });
  };

  // Formulaire d'ajout territoire personnalisé
  const [newTerritoire, setNewTerritoire] = useState({ key: '', label: '', coef: 1.0 });
  const handleAddTerritoire = () => {
    const key = (newTerritoire.key || '').trim().toLowerCase().replace(/\s+/g, '_');
    const label = (newTerritoire.label || '').trim();
    if (!key || !label) {
      toast.error('Renseignez la clé technique ET le libellé du territoire');
      return;
    }
    if ((config?.territoires || []).some(t => t.key === key)) {
      toast.error(`La clé « ${key} » existe déjà`);
      return;
    }
    addItem('territoires', { key, label, coef: Number(newTerritoire.coef) || 1.0 });
    setNewTerritoire({ key: '', label: '', coef: 1.0 });
    toast.success(`Territoire « ${label} » ajouté — pensez à sauvegarder`);
  };

  const confirmDelete = (key, idx, item) => {
    const friendly = item?.label || item?.key || `#${idx + 1}`;
    if (window.confirm(`Supprimer « ${friendly} » ?\nPensez à sauvegarder.`)) {
      removeItem(key, idx);
    }
  };

  if (loading || !config) {
    return (
      <Card data-testid="batico-elig-config-loading">
        <CardContent className="py-12 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />Chargement…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-indigo-200" data-testid="batico-elig-config-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4 text-indigo-600" />
              Modulateurs d&apos;éligibilité BATICO
            </CardTitle>
            <CardDescription>
              Coefficients et règles utilisés par le simulateur RCDS BATICO.
              {config.updated_at && (
                <span className="block mt-1 text-[11px] text-slate-400">
                  Maj : {new Date(config.updated_at).toLocaleString('fr-FR')}
                  {config.updated_by && ` par ${config.updated_by}`}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={reset} disabled={saving} data-testid="batico-elig-reset">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Réinit.
            </Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="batico-elig-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Enregistrer
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <BaticoConfigTabs
          config={config}
          setConfig={setConfig}
          updateList={updateList}
          addItem={addItem}
          removeItem={removeItem}
          confirmDelete={confirmDelete}
          newTerritoire={newTerritoire}
          setNewTerritoire={setNewTerritoire}
          handleAddTerritoire={handleAddTerritoire}
          variant={variant}
        />
      </CardContent>
    </Card>
  );
}
